import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Clock, Users, BookOpen, CheckCircle2, CreditCard, Play, Pencil, Tag, Award } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [quote, setQuote] = useState(null);

  const load = async () => {
    try {
      const [c, e] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/enrollments`).catch(() => ({ data: [] })),
      ]);
      setCourse(c.data);
      setEnrolled(e.data.some((x) => x.course_id === id));
      try { const q = await api.get(`/courses/${id}/quote`); setQuote(q.data); } catch { /* not logged in */ }
    } catch (err) { toast.error("Couldn't load course"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const enrollFree = async () => {
    setEnrolling(true);
    try {
      await api.post(`/courses/${id}/enroll`);
      setEnrolled(true);
      toast.success("Enrolled — start learning now");
      navigate(`/dashboard/courses/${id}/learn`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "payment_required") {
        toast.error("Payment required — redirecting to checkout");
        navigate(`/payments/checkout?context_type=lms&context_id=${id}`);
      } else { toast.error(detail || "Enrollment failed"); }
    } finally { setEnrolling(false); }
  };

  const checkout = () => navigate(`/payments/checkout?context_type=lms&context_id=${id}`);

  const totalLessons = useMemo(() => (course?.modules || []).reduce((acc, m) => acc + (m.lessons?.length || 0), 0), [course]);
  const isOwner = course?.owner_user_id === user?.user_id;
  const isFreeForMe = quote && quote.final_price === 0 && quote.base_price > 0;

  if (!course) return <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>;

  return (
    <div data-testid="course-detail-page">
      <Link to="/dashboard/courses" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO COURSES
      </Link>

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {course.cover_image_url && (
            <div className="rounded-sm overflow-hidden border border-slate-200">
              <img src={course.cover_image_url} alt="" className="w-full max-h-72 object-cover" />
            </div>
          )}
          <div className="kb-card p-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{course.level} · {course.duration_hours} HOURS</div>
            <h1 className="font-serif text-3xl text-kb-ink mt-2 leading-tight">{course.title}</h1>
            <p className="text-kb-ink-muted mt-3 leading-relaxed whitespace-pre-line">{course.description}</p>
            {course.tags?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-1.5">
                {course.tags.map((t) => (
                  <span key={t} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink bg-slate-100 px-2 py-1 rounded-sm">{t}</span>
                ))}
              </div>
            )}
          </div>

          <div className="kb-card p-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CURRICULUM · {course.modules?.length || 0} MODULES · {totalLessons} LESSONS</div>
            <div className="mt-5 space-y-5">
              {(course.modules || []).length === 0 && <div className="text-sm text-kb-ink-muted">Curriculum is being prepared.</div>}
              {(course.modules || []).map((m, mi) => (
                <div key={m.id || mi}>
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">MOD {String(mi+1).padStart(2,'0')}</div>
                    <div className="font-serif text-lg text-kb-ink">{m.title}</div>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(m.lessons || []).map((l, li) => (
                      <li key={l.id || li} className="flex items-center gap-3 text-sm">
                        <BookOpen className="w-3.5 h-3.5 text-kb-ink-light" />
                        <span className="text-kb-ink flex-1">{l.title}</span>
                        <span className="kb-mono text-[10px] text-kb-ink-muted">{l.duration_min}m · {(l.blocks || []).length} parts</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="kb-card p-6 sticky top-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">INSTRUCTOR</div>
            <div className="font-serif text-xl text-kb-ink mt-2">{course.owner_name || course.instructor || "KhojByte Faculty"}</div>

            <div className="mt-5 grid grid-cols-3 gap-2">
              <div>
                <Clock className="w-3.5 h-3.5 text-kb-ink-light" />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-1">Hours</div>
                <div className="font-mono text-kb-primary">{course.duration_hours}</div>
              </div>
              <div>
                <Users className="w-3.5 h-3.5 text-kb-ink-light" />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-1">Students</div>
                <div className="font-mono text-kb-primary">{course.enrolled_count || 0}</div>
              </div>
              <div>
                <Award className="w-3.5 h-3.5 text-amber-500" />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-1">Cert.</div>
                <div className="font-mono text-emerald-700">Yes</div>
              </div>
            </div>

            <div className="mt-6 border-t border-slate-200 pt-4">
              {quote && quote.base_price > 0 && quote.discount_pct > 0 && (
                <div className="text-xs flex items-center gap-2 mb-2 text-emerald-700" data-testid="course-discount-banner">
                  <Tag className="w-3.5 h-3.5" /> {quote.reason}
                </div>
              )}
              {quote && quote.base_price > 0 ? (
                <div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">PRICE</div>
                  <div className="mt-1 flex items-end gap-2">
                    <div className="font-serif text-2xl text-kb-primary">
                      {quote.final_price === 0 ? "FREE" : `NPR ${quote.final_price.toLocaleString()}`}
                    </div>
                    {quote.discount_pct > 0 && quote.final_price > 0 && (
                      <div className="font-mono text-sm text-kb-ink-light line-through">NPR {quote.base_price.toLocaleString()}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="font-serif text-2xl text-emerald-700">FREE</div>
              )}
            </div>

            {isOwner ? (
              <Link to={`/dashboard/courses/${id}/edit`} className="kb-btn-outline w-full justify-center mt-6" data-testid="course-edit-btn">
                <Pencil className="w-3.5 h-3.5" /> Edit course
              </Link>
            ) : enrolled ? (
              <Link to={`/dashboard/courses/${id}/learn`} className="kb-btn-primary w-full justify-center mt-6" data-testid="course-continue-btn">
                <Play className="w-3.5 h-3.5" /> Continue learning
              </Link>
            ) : (quote && quote.final_price > 0) ? (
              <button onClick={checkout} className="kb-btn-primary w-full justify-center mt-6" data-testid="course-checkout-btn">
                <CreditCard className="w-3.5 h-3.5" /> Pay & enroll
              </button>
            ) : (
              <button onClick={enrollFree} disabled={enrolling}
                className="kb-btn-primary w-full justify-center mt-6" data-testid="course-enroll-btn">
                <CheckCircle2 className="w-3.5 h-3.5" /> {enrolling ? "Enrolling…" : (isFreeForMe ? "Enroll (free for partner)" : "Enroll for free")}
              </button>
            )}
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-3 text-center">
              Includes certificate · lifetime access
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
